import React, { useState } from 'react';
import { useAutoFix } from '../../context/AutoFixContext';
import { Sparkles, Terminal, Play, CheckCircle2, ChevronRight, ChevronDown, Clock, Code } from 'lucide-react';

export const ToolRegistry: React.FC = () => {
  const { registeredTools } = useAutoFix();
  const [selectedTool, setSelectedTool] = useState<string | null>(null);
  const [customParams, setCustomParams] = useState<{ [toolName: string]: string }>({});
  const [testResult, setTestResult] = useState<any | null>(null);
  const [executionStats, setExecutionStats] = useState<{ durationMs: number; caller: string } | null>(null);
  const [isExecuting, setIsExecuting] = useState(false);

  const getDefaultParamsForTool = (toolName: string) => {
    switch (toolName) {
      case 'inspectCrashDump':
        return JSON.stringify({ incidentId: 'inc-latest' }, null, 2);
      case 'getStateSnapshot':
        return JSON.stringify({ scope: 'full' }, null, 2);
      case 'reproduceInteraction':
        return JSON.stringify({ bugId: 'bug-1' }, null, 2);
      case 'synthesizePatch':
        return JSON.stringify({ bugId: 'calc-div-zero' }, null, 2);
      case 'auditPatchSafety':
        return JSON.stringify({ patchId: 'patch-1', securityCheck: true }, null, 2);
      case 'applyRuntimeHotpatch':
        return JSON.stringify({ patchId: 'patch-bug-1' }, null, 2);
      case 'runRegressionVerification':
        return JSON.stringify({ patchId: 'patch-bug-1', bugId: 'bug-1' }, null, 2);
      default:
        return '{}';
    }
  };

  const handleTestTool = async (toolName: string) => {
    setIsExecuting(true);
    setTestResult(null);
    setExecutionStats(null);
    const startTime = performance.now();

    try {
      let params = {};
      const rawInput = customParams[toolName] || getDefaultParamsForTool(toolName);
      try {
        params = JSON.parse(rawInput);
      } catch (e) {
        console.warn('Invalid JSON input, using default');
      }

      if (typeof window !== 'undefined' && window.navigator.modelContext) {
        const result = await window.navigator.modelContext.executeTool(
          toolName,
          params,
          'interactive-repl'
        );
        const duration = Math.round(performance.now() - startTime);
        setTestResult(result);
        setExecutionStats({ durationMs: duration, caller: 'interactive-repl' });
      }
    } catch (err: any) {
      const duration = Math.round(performance.now() - startTime);
      setTestResult({ error: err.message || String(err) });
      setExecutionStats({ durationMs: duration, caller: 'interactive-repl' });
    } finally {
      setIsExecuting(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Sparkles size={15} style={{ color: 'var(--accent)' }} />
          <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-1)' }}>
            Registered WebMCP Tools on <code>navigator.modelContext</code>
          </span>
        </div>
        <span style={{ fontSize: 11, color: 'var(--text-3)' }}>
          {registeredTools.length} tools registered · Interactive REPL
        </span>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {registeredTools.map((tool) => {
          const isSelected = selectedTool === tool.name;
          const currentInput = customParams[tool.name] ?? getDefaultParamsForTool(tool.name);

          return (
            <div
              key={tool.name}
              className="card"
              style={{
                padding: 12,
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div
                    style={{
                      background: 'var(--accent-soft)',
                      padding: 6,
                      borderRadius: 6,
                      color: 'var(--accent)',
                    }}
                  >
                    <Terminal size={14} />
                  </div>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-1)', fontFamily: 'var(--font-mono)' }}>
                      {tool.name}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text-2)', marginTop: 2 }}>
                      {tool.description}
                    </div>
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <button
                    className="btn btn-sm btn-green"
                    onClick={() => {
                      setSelectedTool(tool.name);
                      handleTestTool(tool.name);
                    }}
                    disabled={isExecuting}
                    style={{ padding: '3px 8px', fontSize: 11 }}
                  >
                    <Play size={10} /> Execute
                  </button>

                  <button
                    type="button"
                    onClick={() => setSelectedTool(isSelected ? null : tool.name)}
                    style={{ background: 'none', border: 'none', color: 'var(--text-3)', cursor: 'pointer', padding: 2 }}
                  >
                    {isSelected ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  </button>
                </div>
              </div>

              {/* Collapsible Tool Details & Interactive REPL */}
              {isSelected && (
                <div
                  style={{
                    marginTop: 6,
                    padding: 12,
                    background: 'var(--bg-input)',
                    borderRadius: 6,
                    border: '1px solid var(--border)',
                    fontSize: 11,
                    fontFamily: 'var(--font-mono)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 10,
                  }}
                >
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                      <span style={{ color: 'var(--text-2)', fontWeight: 600 }}>Execute with Custom Arguments (JSON):</span>
                      <button
                        className="btn btn-sm"
                        onClick={() => handleTestTool(tool.name)}
                        disabled={isExecuting}
                        style={{ padding: '2px 8px', fontSize: 10 }}
                      >
                        <Play size={9} /> Run Now
                      </button>
                    </div>
                    <textarea
                      value={currentInput}
                      onChange={(e) =>
                        setCustomParams((prev) => ({ ...prev, [tool.name]: e.target.value }))
                      }
                      style={{
                        width: '100%',
                        height: 60,
                        background: '#090d16',
                        border: '1px solid var(--border)',
                        borderRadius: 4,
                        color: 'var(--accent)',
                        fontFamily: 'var(--font-mono)',
                        fontSize: 11,
                        padding: '6px 8px',
                        resize: 'vertical',
                      }}
                      spellCheck={false}
                    />
                  </div>

                  <div>
                    <span style={{ color: 'var(--text-3)' }}>Tool Input Schema:</span>
                    <pre style={{ margin: '4px 0 0', color: 'var(--text-2)', overflowX: 'auto', background: '#090d16', padding: 8, borderRadius: 4 }}>
                      {JSON.stringify(tool.inputSchema, null, 2)}
                    </pre>
                  </div>

                  {testResult && (
                    <div style={{ borderTop: '1px solid var(--border)', paddingTop: 8 }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                        <span style={{ color: 'var(--accent)', fontWeight: 600 }}>Execution Response:</span>
                        {executionStats && (
                          <span style={{ color: 'var(--green)', fontSize: 10, display: 'flex', alignItems: 'center', gap: 4 }}>
                            <Clock size={10} /> {executionStats.durationMs}ms · caller: {executionStats.caller}
                          </span>
                        )}
                      </div>
                      <pre style={{ margin: '4px 0 0', color: 'var(--text-1)', overflowX: 'auto', background: '#090d16', padding: 8, borderRadius: 4 }}>
                        {JSON.stringify(testResult, null, 2)}
                      </pre>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
